import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, lessonsAPI, studentsAPI, activityLogsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Users,
  GraduationCap,
  MapPin,
  Calendar,
  Award,
  TrendingUp,
  Clock,
  CheckCircle,
  Loader2,
  Bell,
  BellRing,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Activity,
  UserPlus,
  Edit,
  Trash2,
  Upload,
  FileCheck,
  School,
  Download,
  Globe,
  MousePointerClick,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, color, onClick, subtitle }) => (
  <Card 
    className="stats-card group cursor-pointer" 
    onClick={onClick}
    data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color} transition-transform group-hover:scale-110`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Activity action icons and colors
const activityConfig = {
  created: { icon: UserPlus, color: 'text-green-500', bg: 'bg-green-50' },
  updated: { icon: Edit, color: 'text-blue-500', bg: 'bg-blue-50' },
  deleted: { icon: Trash2, color: 'text-red-500', bg: 'bg-red-50' },
  uploaded: { icon: Upload, color: 'text-purple-500', bg: 'bg-purple-50' },
  completed: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  approved: { icon: FileCheck, color: 'text-green-500', bg: 'bg-green-50' },
  rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  registered: { icon: School, color: 'text-teal-500', bg: 'bg-teal-50' },
  rescheduled: { icon: Calendar, color: 'text-amber-500', bg: 'bg-amber-50' },
  enrolled: { icon: GraduationCap, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  downloaded: { icon: Download, color: 'text-cyan-500', bg: 'bg-cyan-50' },
  public_event: { icon: MousePointerClick, color: 'text-violet-500', bg: 'bg-violet-50' },
  default: { icon: Activity, color: 'text-slate-500', bg: 'bg-slate-50' },
};

// Activity Log Item component (used for both Activity Log and Alerts)
const ActivityItem = ({ activity, isUnread = false, showSource = false, getSourceLabel }) => {
  const config = activityConfig[activity.action] || activityConfig.default;
  const IconComponent = config.icon;
  const isPublic = activity.is_public || activity.scope === 'public';
  
  // Format relative time
  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <div 
      className={`p-3 rounded-lg ${config.bg} transition-colors ${isUnread ? 'ring-2 ring-primary/20' : ''}`}
      data-testid={`activity-${activity.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0">
          <IconComponent className={`w-5 h-5 mt-0.5 ${config.color}`} />
          {isUnread && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-medium text-sm text-slate-900 capitalize truncate">
                {isPublic ? 'Public Action' : `${activity.action} ${activity.entity_type?.replace(/_/g, ' ')}`}
              </p>
              {isPublic && (
                <span className="text-[10px] font-semibold text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded uppercase flex-shrink-0">
                  Public
                </span>
              )}
            </div>
            <span className="text-xs text-slate-400 flex-shrink-0">
              {formatRelativeTime(activity.created_at)}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
            {activity.details || (isPublic ? 'Public visitor interaction' : 'No details')}
          </p>
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-slate-400">
              {isPublic ? (
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  Public Visitor
                </span>
              ) : (
                `by ${activity.user_name}`
              )}
            </p>
            {showSource && getSourceLabel && (
              <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                {isPublic ? 'Public' : getSourceLabel(activity.entity_type)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayLessons, setTodayLessons] = useState([]);
  const [recentStudents, setRecentStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({ title: '', items: [] });
  
  // Real-time activity state (unified source for both Activity Log and Alerts)
  const [activityLogs, setActivityLogs] = useState([]);
  const [readAlertIds, setReadAlertIds] = useState(() => {
    // Load read alert IDs from localStorage
    try {
      const stored = localStorage.getItem('readAlertIds');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  
  // Premium toggle: Include public events
  const [includePublic, setIncludePublic] = useState(() => {
    try {
      return localStorage.getItem('dashboard_include_public') === '1';
    } catch {
      return false;
    }
  });
  
  // Ref for polling interval
  const pollingRef = useRef(null);
  const lastActivityTimestamp = useRef(null);
  const includePublicRef = useRef(includePublic); // Ref to access current value in callbacks

  // Alert-worthy action types (these appear in Alerts tab)
  const ALERT_ACTIONS = [
    'created', 'updated', 'deleted', 'enrolled',
    'completed', 'rescheduled', 'uploaded',
    'registered', 'approved', 'rejected',
    'generated', 'downloaded',
    'bulk_created', 'bulk_updated',
    'public_event'  // Include public events when toggle is ON
  ];

  // Priority levels for alert sorting
  const ALERT_PRIORITY = {
    deleted: 1,      // Critical
    rejected: 1,     // Critical
    rescheduled: 2,  // Warning
    updated: 3,      // Info
    created: 3,      // Info
    uploaded: 3,     // Info
    registered: 3,   // Info
    completed: 4,    // Success (lower priority)
    approved: 4,     // Success
    enrolled: 4,     // Info
    downloaded: 4,   // Info
    public_event: 5, // Public visitor interaction (lowest)
    default: 5
  };

  // Map entity types to source labels
  const getSourceLabel = (entityType) => {
    const sourceMap = {
      student: 'Students',
      lesson: 'Lessons',
      program: 'Programs',
      school_registration: 'Schools',
      schools_flyer: 'Schools',
      schools_carousel: 'Schools',
      certificate: 'Certificates',
      landing_image: 'Media',
      setting: 'System',
      user: 'System',
    };
    return sourceMap[entityType?.toLowerCase()] || 'System';
  };

  // Derive alerts from activity logs with priority sorting
  const alerts = useMemo(() => {
    const filtered = activityLogs.filter(activity => 
      ALERT_ACTIONS.includes(activity.action?.toLowerCase())
    );
    
    // Sort by: unread first, then priority, then newest
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return filtered.sort((a, b) => {
      const aIsUnread = !readAlertIds.includes(a.id) && new Date(a.created_at) > oneDayAgo;
      const bIsUnread = !readAlertIds.includes(b.id) && new Date(b.created_at) > oneDayAgo;
      
      // Unread first
      if (aIsUnread && !bIsUnread) return -1;
      if (!aIsUnread && bIsUnread) return 1;
      
      // Then by priority
      const aPriority = ALERT_PRIORITY[a.action?.toLowerCase()] || ALERT_PRIORITY.default;
      const bPriority = ALERT_PRIORITY[b.action?.toLowerCase()] || ALERT_PRIORITY.default;
      if (aPriority !== bPriority) return aPriority - bPriority;
      
      // Then by date (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [activityLogs, readAlertIds]);

  // Count unread alerts (not in readAlertIds and within last 24 hours)
  const unreadAlertCount = useMemo(() => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return alerts.filter(alert => {
      const isRecent = new Date(alert.created_at) > oneDayAgo;
      const isUnread = !readAlertIds.includes(alert.id);
      return isRecent && isUnread;
    }).length;
  }, [alerts, readAlertIds]);

  // Mark all alerts as read when "Clear" button is clicked
  const handleClearAlerts = useCallback(() => {
    const alertIds = alerts.map(a => a.id);
    const newReadIds = [...new Set([...readAlertIds, ...alertIds])];
    setReadAlertIds(newReadIds);
    // Persist to localStorage (keep last 100)
    localStorage.setItem('readAlertIds', JSON.stringify(newReadIds.slice(-100)));
    toast.success('All alerts marked as read');
  }, [alerts, readAlertIds]);

  // Tab change - NO auto-clear, just switch tabs
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  // Handle toggle change
  const handleIncludePublicChange = useCallback((checked) => {
    setIncludePublic(checked);
    includePublicRef.current = checked;
    localStorage.setItem('dashboard_include_public', checked ? '1' : '0');
    // Immediately refetch with new setting
    loadActivityLogs(false, checked);
  }, []);

  // Load activity logs with cache-bust
  const loadActivityLogs = useCallback(async (silent = false, overrideIncludePublic = null) => {
    try {
      const includePublicValue = overrideIncludePublic !== null ? overrideIncludePublic : includePublicRef.current;
      const response = await activityLogsAPI.getAll({ 
        limit: 20, 
        t: Date.now(),
        include_public: includePublicValue 
      });
      const newActivities = response.data || [];
      
      // Check if there are new activities (for toast notification)
      if (!silent && lastActivityTimestamp.current && newActivities.length > 0) {
        const latestActivity = newActivities[0];
        if (new Date(latestActivity.created_at) > new Date(lastActivityTimestamp.current)) {
          // New activity detected - silently refresh stats too
          loadDashboardData(true);
        }
      }
      
      // Update timestamp
      if (newActivities.length > 0) {
        lastActivityTimestamp.current = newActivities[0].created_at;
      }
      
      // Force rerender with new array reference
      setActivityLogs([...newActivities]);
    } catch (error) {
      console.error('Failed to load activity logs:', error);
    }
  }, []);

  // Combined refresh function for real-time updates
  const refreshRealTimeData = useCallback(async (silent = true) => {
    await loadActivityLogs(silent);
  }, [loadActivityLogs]);

  // Keep ref in sync with state
  useEffect(() => {
    includePublicRef.current = includePublic;
  }, [includePublic]);

  // Manual refresh handler
  const handleManualRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadDashboardData(true),
        refreshRealTimeData(false),
      ]);
      toast.success('Dashboard refreshed');
    } catch (error) {
      toast.error('Failed to refresh');
    }
    setRefreshing(false);
  }, [refreshRealTimeData]);

  useEffect(() => {
    // Initial load
    loadDashboardData();
    refreshRealTimeData(true);
    
    // Set up polling interval for real-time updates (every 10 seconds)
    pollingRef.current = setInterval(() => {
      // Only poll when page is visible
      if (!document.hidden) {
        refreshRealTimeData(true);
      }
    }, 10000);
    
    // Fallback: Full dashboard refresh every 60 seconds
    const fullRefreshInterval = setInterval(() => {
      if (!document.hidden) {
        loadDashboardData(true);
      }
    }, 60000);
    
    // Handle visibility change - fetch immediately when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshRealTimeData(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for activity:refresh event for instant updates after admin actions
    const handleActivityRefresh = () => {
      refreshRealTimeData(true);
    };
    window.addEventListener('activity:refresh', handleActivityRefresh);
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      clearInterval(fullRefreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('activity:refresh', handleActivityRefresh);
    };
  }, [refreshRealTimeData]);

  const loadDashboardData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [statsRes, lessonsRes, studentsRes] = await Promise.all([
        dashboardAPI.getStats(),
        lessonsAPI.getAll({ date: new Date().toISOString().split('T')[0] }),
        studentsAPI.getAll({ status: 'active' }),
      ]);
      
      setStats(statsRes.data);
      setTodayLessons(lessonsRes.data.slice(0, 5));
      setRecentStudents(studentsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await handleManualRefresh();
  };

  const handleStatClick = async (type) => {
    try {
      let items = [];
      let title = '';
      
      switch (type) {
        case 'students':
          const studentsRes = await studentsAPI.getAll({});
          items = studentsRes.data.map(s => ({
            name: s.name,
            subtitle: `${s.program_name || 'No program'} - ${s.status}`,
            onClick: () => navigate(`/students/${s.id}`)
          }));
          title = 'All Students';
          break;
        case 'lessons-today':
          const lessonsRes = await lessonsAPI.getAll({ date: new Date().toISOString().split('T')[0] });
          items = lessonsRes.data.map(l => ({
            name: `${l.student_name} - Lesson #${l.lesson_number}`,
            subtitle: `${l.coach_name || 'No coach'} - ${l.status}`,
            onClick: () => navigate('/lessons')
          }));
          title = "Today's Lessons";
          break;
        case 'completed':
          const completedRes = await studentsAPI.getAll({});
          items = completedRes.data.filter(s => s.progress_percent >= 100).map(s => ({
            name: s.name,
            subtitle: `${s.program_name || 'Program'} - Completed`,
            onClick: () => navigate(`/students/${s.id}`)
          }));
          title = 'Completed Programs';
          break;
        default:
          return;
      }
      
      setDialogData({ title, items });
      setDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-slate-400">Preparing your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Welcome back! Here&apos;s your academy overview.</p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh} 
          disabled={refreshing}
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Real-time Activity & Alerts Panel */}
      <Card className="border-l-4 border-l-primary" data-testid="alerts-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Real-time Activity
              {activityLogs.length > 0 && (
                <span className="text-xs text-slate-400 font-normal ml-2">
                  Updated every 10s
                </span>
              )}
              {includePublic && (
                <Badge variant="secondary" className="ml-2 text-[10px] font-medium bg-violet-100 text-violet-700">
                  <Globe className="w-3 h-3 mr-1" />
                  Including Public
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-3">
              {/* Premium Toggle: Show Public Events */}
              <div className="flex items-center gap-2">
                <label 
                  htmlFor="include-public-toggle" 
                  className="text-xs text-slate-500 cursor-pointer"
                >
                  Public Events
                </label>
                <Switch
                  id="include-public-toggle"
                  checked={includePublic}
                  onCheckedChange={handleIncludePublicChange}
                  data-testid="include-public-toggle"
                  className="data-[state=checked]:bg-violet-500"
                />
              </div>
              {unreadAlertCount > 0 && (
                <Badge variant="destructive" data-testid="unread-count">
                  {unreadAlertCount} new
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-activity-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-2">
              <TabsTrigger value="activity" data-testid="activity-tab">
                <Activity className="w-4 h-4 mr-2" />
                Activity Log
              </TabsTrigger>
              <TabsTrigger value="notifications" data-testid="notifications-tab">
                {unreadAlertCount > 0 ? (
                  <BellRing className="w-4 h-4 mr-2 text-primary" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Alerts {unreadAlertCount > 0 && `(${unreadAlertCount})`}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="activity" className="mt-0">
              {/* Subtitle for Activity Log */}
              <p className="text-xs text-slate-400 mb-3 px-1">
                {includePublic ? 'Admin + public events (read-only)' : 'All actions recorded (read-only)'}
              </p>
              {activityLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium text-slate-500">All caught up</p>
                  <p className="text-sm mt-1">
                    {includePublic 
                      ? 'New admin or public activity will appear here' 
                      : 'New activity will appear here as it happens'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="activity-list">
                  {activityLogs.map((activity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="notifications" className="mt-0">
              {/* Sticky header for Alerts */}
              <div className="sticky top-0 bg-white z-10 pb-2 -mt-1">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400 px-1">Items that need your attention</p>
                  <div className="flex items-center gap-2">
                    {unreadAlertCount > 0 && (
                      <span className="text-xs font-medium text-primary">
                        {unreadAlertCount} Unread
                      </span>
                    )}
                    {alerts.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={handleClearAlerts}
                        data-testid="clear-alerts-btn"
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-40 text-emerald-400" />
                  <p className="font-medium text-slate-500">No alerts right now</p>
                  <p className="text-sm mt-1">You're all caught up.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="alerts-list">
                  {alerts.map((activity) => (
                    <ActivityItem 
                      key={activity.id} 
                      activity={activity} 
                      isUnread={!readAlertIds.includes(activity.id)}
                      showSource={true}
                      getSourceLabel={getSourceLabel}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Students"
          value={stats?.total_students || 0}
          subtitle={`${stats?.active_students || 0} active`}
          icon={Users}
          color="bg-primary"
          onClick={() => handleStatClick('students')}
        />
        <StatCard
          title="Lessons Today"
          value={stats?.lessons_today || 0}
          subtitle="Scheduled sessions"
          icon={Calendar}
          color="bg-secondary"
          onClick={() => handleStatClick('lessons-today')}
        />
        <StatCard
          title="Completed Programs"
          value={stats?.completed_programs || 0}
          subtitle="Students graduated"
          icon={Award}
          color="bg-accent"
          onClick={() => handleStatClick('completed')}
        />
        <StatCard
          title="Upcoming Lessons"
          value={stats?.upcoming_lessons || 0}
          subtitle="Scheduled ahead"
          icon={Clock}
          color="bg-emerald-500"
          onClick={() => navigate('/lessons')}
        />
      </div>

      {/* Second row stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Coaches"
          value={stats?.total_coaches || 0}
          icon={GraduationCap}
          color="bg-violet-500"
          onClick={() => navigate('/coaches')}
        />
        <StatCard
          title="Programs"
          value={stats?.total_programs || 0}
          icon={TrendingUp}
          color="bg-pink-500"
          onClick={() => navigate('/programs')}
        />
        <StatCard
          title="Locations"
          value={stats?.total_locations || 0}
          icon={MapPin}
          color="bg-amber-500"
          onClick={() => navigate('/locations')}
        />
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Lessons */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Today&apos;s Lessons
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayLessons.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No lessons scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayLessons.map((lesson) => (
                  <div
                    key={lesson.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate('/lessons')}
                    data-testid={`lesson-${lesson.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                        #{lesson.lesson_number}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{lesson.student_name}</p>
                        <p className="text-sm text-slate-500">{lesson.coach_name}</p>
                      </div>
                    </div>
                    <Badge variant={lesson.status === 'completed' ? 'default' : 'secondary'}>
                      {lesson.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Students */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Student Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentStudents.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No students found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentStudents.map((student) => (
                  <div
                    key={student.id}
                    className="cursor-pointer hover:bg-slate-50 p-3 rounded-lg transition-colors"
                    onClick={() => navigate(`/students/${student.id}`)}
                    data-testid={`student-progress-${student.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs">
                          {student.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{student.name}</p>
                          <p className="text-xs text-slate-500">{student.program_name}</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-primary">
                        {Math.round(student.progress_percent)}%
                      </span>
                    </div>
                    <Progress value={student.progress_percent} className="h-2" />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-slate-400">
                        {student.lessons_completed}/{student.lessons_total} lessons
                      </span>
                      {student.progress_percent >= 100 && (
                        <span className="text-xs text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Completed
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogData.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {dialogData.items.length === 0 ? (
              <p className="text-center py-8 text-slate-400">No items found</p>
            ) : (
              dialogData.items.map((item, i) => (
                <div
                  key={i}
                  className="p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => {
                    setDialogOpen(false);
                    item.onClick();
                  }}
                >
                  <p className="font-medium text-slate-900">{item.name}</p>
                  <p className="text-sm text-slate-500">{item.subtitle}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
